import { Button, useToast } from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";

/**
 * Sosyal giriş aksiyonları (CLOS-336).
 *
 * Kayıt ve giriş ekranları AYNI bileşeni kullanır: iki yerde ayrı yazılsaydı biri
 * güncellenir, diğeri unutulurdu.
 */
type Providers = { google: boolean };

export function SocialAuthButtons({ label }: { label: string }) {
  const toast = useToast();
  const [yonlendiriliyor, setYonlendiriliyor] = useState(false);

  const {
    data: saglayicilar,
    isPending,
    error: sorguHatasi,
    refetch,
  } = useQuery({
    queryKey: ["session", "providers"],
    queryFn: async () => (await api<Providers>("/api/session/providers")).data,
  });

  // Cevap gelene kadar hiçbir şey çizilmiyor: yanıp sönen bir düğme, olmayan bir seçeneği
  // varmış gibi gösterir.
  if (isPending) return null;

  /**
   * ⚠ "SORULAMADI" ile "YAPILANDIRILMAMIŞ" AYNI ŞEY DEĞİL (4 Eylül 2026 kesintisi).
   *
   * Bileşen eskiden tek satır soruyordu: `if (!saglayicilar?.google) return null`. Sorgu
   * DÜŞTÜĞÜNDE de `data` `undefined` olduğu için düğme sessizce kayboluyordu — hiçbir
   * uyarı, hiçbir iz, yeniden deneme yolu da yok.
   *
   * Sunucu bir kesinti sırasında yeniden başlarken sayfayı açan kullanıcı, Google ile
   * girişin KALDIRILDIĞINI sanıyordu. Gerçekte özellik yerindeydi; yalnız o an tek bir
   * istek düşmüştü. Arızanın "özellik yok" gibi görünmesi bu depodaki en pahalı kusur
   * sınıfı ve burada tam ortasındaydı.
   *
   * Artık ayrım açık: sorulamadıysa sebebi yazılıyor ve yeniden deneme sunuluyor.
   */
  if (sorguHatasi) {
    return (
      <div role="alert" className="flex flex-col items-center gap-2">
        <p className="text-center text-xs text-fg-soft">Giriş seçenekleri şu an yüklenemedi.</p>
        {/* ⚠ Kit düğmesi: sayfa katmanında ham HTML elementi kullanılmıyor (kit bekçisi). */}
        <Button type="button" variant="ghost" size="sm" onClick={() => void refetch()}>
          Tekrar dene
        </Button>
      </div>
    );
  }

  // Yapılandırılmamış sağlayıcının düğmesi ÇİZİLMEZ: kullanıcıyı çalışmayan bir yola
  // sokmak, hiç göstermemekten kötüdür — hata sağlayıcının ekranında çıkar ve kimse
  // sebebini anlamaz. Bu dal artık YALNIZ sunucu "yapılandırılmadı" dediğinde koşuyor.
  if (!saglayicilar?.google) return null;

  const basla = async () => {
    // Tekrarlı tıklama İKİNCİ bir OAuth turu başlatmamalı: her tur yeni bir state
    // üretir ve öncekini geçersiz kılar, kullanıcı da sebepsiz bir hataya düşer.
    if (yonlendiriliyor) return;
    setYonlendiriliyor(true);

    try {
      const { data: sonuc } = await api<{ url: string }>("/api/session/social", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ provider: "google" }),
      });
      // Hedef YALNIZ sunucudan gelir; istemci bir adres uydurmaz.
      window.location.assign(sonuc.url);
    } catch (error) {
      // Sahte başarı yok: yönlendirme olmadıysa kullanıcı formda kalır ve sebebini görür.
      setYonlendiriliyor(false);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : "Google ile bağlantı kurulamadı. Lütfen tekrar deneyin.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3" aria-hidden="true">
        <span className="h-px flex-1 bg-muted" />
        <span className="text-xs text-fg-faint">veya</span>
        <span className="h-px flex-1 bg-muted" />
      </div>
      <Button type="button" variant="secondary" loading={yonlendiriliyor} onClick={basla}>
        {label}
      </Button>
    </div>
  );
}
